import React, { Component } from 'react';
import {
  connect,
  ConnectedProps
} from 'react-redux';
import {
  get,
  isEmpty
} from 'lodash';
import moment from 'moment';
import {
  ELSCommonUIConstants,
  ELSLoggingService,
  ELSPageLoader,
  ELSTokenHelper,
  ELSTokenServiceRegistrar
} from '@els/els-ui-common-react';
import { studyActions } from '../../redux/student-study/studentStudy.actions';
import { RoutePath } from '../../components/app/app.constants';
import {
  AppAction,
  Application
} from '../../apis/eols-app-link/eols-app-link.constants';
import {
  AppLinkCookies,
  AppLinkData
} from '../../apis/eols-app-link/eols-app-link.dtos';
import { locationActions } from '../../redux/location/location.actions';
import {
  AppLinkRedirectErrorMessage,
  AppLinkRedirectErrorMessages,
  AssessmentPlayerStatus
} from './app-link-redirect.constants';
import { ELSTokenUser } from '../../models/els.dtos';
import { registerMouseFlowService } from '../../components/app/app.config';
import { studySelectors } from '../../redux/student-study/studentStudy.selectors';
import { locationSelectors } from '../../redux/location/location.selectors';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { AnalyticsAction } from '../../models/analytics.models';
import {
  addSearchParams,
} from '../../utilities/app.utilities';
import { HashLinkDto } from '../../apis/eols-app-link/eols-app-link.utilities';

const mapDispatchToProps = {
  fetchAppLinkData: studyActions.fetchAppLinkData,
  fetchAssignments: studyActions.fetchAssignments,
  fetchCourseSection: studyActions.fetchCourseSection,
  fetchAllAppFeatureFlags: studyActions.fetchAllAppFeatureFlags,
  replaceUrl: locationActions.replaceURL,
  setIsbns: studyActions.setIsbns,
  setUser: studyActions.setUser,
  setRegisteredToken: studyActions.setRegisteredToken,
  resetStateOnLaunch: studyActions.resetStateOnLaunch,
  trackAction: studyActions.trackAction,
  trackAdobeAction: studyActions.trackAdobeAction,
  setEnableDeepLink: studyActions.setEnableDeepLink,
  fetchHashLinkAction: studyActions.fetchHashLinkAction,
  fetchAssignmentAction: studyActions.fetchAssignmentAction,
  fetchEvolveUsers: studyActions.fetchEvolveUsers,
  setAppLinkCookies: studyActions.setAppLinkCookies,
  setEcrIdAction: studyActions.setEcrIdAction,
  navigateToApp: studyActions.navigateToApp,
};

const mapStateToProps = state => ({
  appLinkCookies: studySelectors.getAppLinkCookies(state),
  location: locationSelectors.getLocation(state)
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type AppLinkRedirectProps = PropsFromRedux;

export interface AppLinkRedirectState {
  hasError: boolean;
  errorMessage: string;
}

const fileName = 'AppLinkRedirect.page';

type AppLinkMethodProps = AppLinkCookies;

export class AppLinkRedirect extends Component<AppLinkRedirectProps, AppLinkRedirectState> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    const {
      appLinkCookies,
      setAppLinkCookies,
      location,
      fetchHashLinkAction
    } = this.props;

    const { linkHash } = location.query;

    if (linkHash) {
      fetchHashLinkAction(linkHash).then((response: HashLinkDto) => {
        setAppLinkCookies(response);
        this.launchApp(response, true);
      });
    } else if (this.isRedirectValid(appLinkCookies)) {
      this.launchApp(appLinkCookies, false);
    }
  }

  isRedirectValid = (config: AppLinkMethodProps): boolean => {

    const { token, linkId } = config;

    if (!token || !linkId) {
      this.logError(AppLinkRedirectErrorMessage.MISSING_COOKIES, null);
      return false;
    }

    const tokenExpireDate = ELSTokenHelper.getExpirationDateFromToken(token);
    const now = moment();
    if (now.isAfter(tokenExpireDate)) {
      this.logError(AppLinkRedirectErrorMessage.EXPIRED_TOKEN, null);
      return false;
    }

    const tokenUser: ELSTokenUser = ELSTokenHelper.getUserInfoFromToken(token);
    if (isEmpty(tokenUser)) {
      this.logError(AppLinkRedirectErrorMessage.INVALID_TOKEN, null);
      return false;
    }

    const validRoles = [
      ELSCommonUIConstants.userType.Student,
      ELSCommonUIConstants.userType.Instructor
    ];

    if (!validRoles.includes(tokenUser.role)) {
      this.logError(AppLinkRedirectErrorMessage.UNSUPPORTED_USER_ROLE, null);
      return false;
    }

    return true;
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  launchApp = (config: AppLinkMethodProps, isHashLink: boolean) => {

    const { token, linkId } = config;

    const {
      fetchAppLinkData,
      fetchCourseSection,
      setIsbns,
      setUser,
      setRegisteredToken,
      fetchAllAppFeatureFlags,
      fetchEvolveUsers,
      trackAction,
      setEcrIdAction,
      navigateToApp
    } = this.props;

    ELSTokenServiceRegistrar.register(token);
    setRegisteredToken(token);
    const tokenUser: ELSTokenUser = ELSTokenHelper.getUser();
    const courseSectionId: string = tokenUser.appParams.courseId ? tokenUser.appParams.courseId.toString() : null;

    setUser({
      userId: tokenUser.userId.toString(),
      courseId: tokenUser.appParams.courseId ? tokenUser.appParams.courseId.toString() : null,
      roleId: tokenUser.role,
      isbns: tokenUser.appParams.isbns
    });

    setIsbns(tokenUser.appParams.isbns);
    registerMouseFlowService();

    fetchEvolveUsers(tokenUser.userId.toString());

    return fetchAppLinkData(linkId).then((appLinkData: AppLinkData) => {
      return Promise.all([
        fetchAllAppFeatureFlags(),
        fetchCourseSection(courseSectionId)
      ])
        .then(([flags]) => {

          trackAction({
            action: AnalyticsAction.APP_LINK_LAUNCH,
            props: {
              linkType: appLinkData.action,
              srcApp: appLinkData.srcApp,
              targetApp: appLinkData.targetApp
            }
          });

          if (appLinkData.outPostBody && [
            'CHATBOT',
            'CHATBOT_ADMIN',
            'AUTH',
          ].includes(appLinkData.outPostBody.targetPage)) {
            trackAction({
              action: AnalyticsAction.AI_CHAT_LAUNCH_REDIRECTED,
              props: {
                ref: 'APP_LINK'
              }
            });
            navigateToApp({
              action: appLinkData.action,
              app: Application.NHE_AI,
              body: appLinkData.outPostBody,
              altSrcApp: appLinkData.srcApp,
              parentLinkId: appLinkData.parentLinkId,
              includeLinkHash: isHashLink
            });
            return Promise.resolve();
          }

          const isMaintenance = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(flags, FEATURE_FLAG.IS_SITE_DOWN_FOR_MAINTENANCE, courseSectionId);
          if (isMaintenance) {
            return Promise.resolve();
          }

          const ecrId = appLinkData && appLinkData.outPostBody ? appLinkData.outPostBody.ecrId : null;
          if (ecrId) {
            setEcrIdAction(ecrId);
          }

          if (appLinkData.targetApp === Application.STUDENT_STUDY) {
            this.handleAppLinkLaunch(appLinkData);
            return Promise.resolve();
          }
          if (appLinkData.srcApp === Application.STUDENT_STUDY) {
            this.handleAppLinkReturn(appLinkData);
            return Promise.resolve();
          }

          return Promise.resolve();
        });
    }).catch((error) => {
      this.logError(AppLinkRedirectErrorMessage.REQUEST_FAILED, error);
    });
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleAppLinkLaunch = (appLinkData: AppLinkData) => {
    const {
      replaceUrl,
      fetchAppLinkData,
    } = this.props;

    if (appLinkData.action === AppAction.LAUNCH) {
      return replaceUrl(RoutePath.EAQ_STUDENT_STUDY);
    }

    if (appLinkData.action === AppAction.PERFORMANCE_VIEW
      && appLinkData.outPostBody && appLinkData.outPostBody.targetPage === 'EAQ_STUDENT_TOPIC_REPORT') {
      return replaceUrl(RoutePath.EAQ_STUDENT_TOPIC_REPORT);
    }

    if ((appLinkData.action === AppAction.ASSESSMENT_START)
      && appLinkData.targetApp === Application.STUDENT_STUDY) {
      return replaceUrl(RoutePath.ASSESSMENT_START);
    }

    if ((appLinkData.action === AppAction.ASSESSMENT_PERFORMANCE_VIEW)
      && appLinkData.targetApp === Application.STUDENT_STUDY
      && appLinkData.outPostBody
      && appLinkData.outPostBody.assignmentId
    ) {
      return replaceUrl(RoutePath.EAQ_STUDENT_PERFORMANCE_DETAILS);
    }

    if ((appLinkData.action === AppAction.ASSESSMENT_PERFORMANCE_VIEW)
      && appLinkData.targetApp === Application.STUDENT_STUDY
      && appLinkData.srcApp === Application.NHE_ASSESSMENT_PLAYER
      && !appLinkData.outPostBody
    ) {
      return fetchAppLinkData(appLinkData.parentLinkId).then((parentData: AppLinkData) => {
        return replaceUrl(addSearchParams(
          RoutePath.EAQ_STUDENT_PERFORMANCE_DETAILS, { assessmentId: parentData.outPostBody.assessmentId, parentLinkId: appLinkData.parentLinkId }
        ));
      });
    }

    return Promise.resolve();
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  handleAppLinkReturn = (appLinkData: AppLinkData) => {
    const {
      replaceUrl,
    } = this.props;

    const isRedirectFromAssessmentPlayer = appLinkData.action === AppAction.ASSESSMENT_START
      && appLinkData.targetApp === Application.NHE_ASSESSMENT_PLAYER;

    if (appLinkData.action === AppAction.ASSESSMENT_START || AppAction.ASSESSMENT_PERFORMANCE_VIEW) {
      if (appLinkData.outPostBody
        && appLinkData.outPostBody.ref === RoutePath.EAQ_STUDENT_TOPIC_REPORT
        && appLinkData.returnPostBody.assessmentStatus === AssessmentPlayerStatus.COMPLETED) {
        return replaceUrl(addSearchParams(
          RoutePath.EAQ_STUDENT_PERFORMANCE_DETAILS, { assessmentId: appLinkData.outPostBody.assessmentId }
        ));
      }

      if (appLinkData.outPostBody
        && appLinkData.outPostBody.ref === RoutePath.EAQ_STUDENT_TOPIC_REPORT
        && appLinkData.returnPostBody.assessmentStatus === AssessmentPlayerStatus.IN_PROGRESS) {
        return replaceUrl(RoutePath.EAQ_STUDENT_TOPIC_REPORT);
      }

      if (isRedirectFromAssessmentPlayer
        && appLinkData.returnPostBody
        && appLinkData.returnPostBody.assessmentStatus === AssessmentPlayerStatus.COMPLETED) {
        return replaceUrl(addSearchParams(
          RoutePath.EAQ_STUDENT_PERFORMANCE_DETAILS, { assessmentId: appLinkData.outPostBody.assessmentId }
        ));
      }

      if (isRedirectFromAssessmentPlayer
        && appLinkData.returnPostBody
        && appLinkData.returnPostBody.assessmentStatus === AssessmentPlayerStatus.IN_PROGRESS
        && appLinkData.outPostBody
        && appLinkData.outPostBody.srcAssessmentId
      ) {
        return replaceUrl(addSearchParams(
          RoutePath.EAQ_STUDENT_PERFORMANCE_DETAILS, { assessmentId: appLinkData.outPostBody.srcAssessmentId }
        ));
      }

      return replaceUrl(RoutePath.EAQ_STUDENT_STUDY);
    }

    return Promise.resolve();
  };

  logError = (errorType: AppLinkRedirectErrorMessage, error): void => {
    const url = get(error, 'config.url', '');
    const method = get(error, 'config.method', '');
    const status = get(error, 'response.status', '');
    const errorMessage = AppLinkRedirectErrorMessages[errorType];
    this.props.trackAction({
      action: AnalyticsAction.APP_LINK_ERROR,
      props: {
        type: errorType,
        url,
        method,
        status
      }
    });
    ELSLoggingService.error(fileName, errorMessage);
    this.setState({
      hasError: true,
      errorMessage
    });
  };

  render() {
    const { hasError, errorMessage } = this.state;

    if (hasError) {
      return (
        <div className="u-els-padding-3x">
          <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary" data-testid="ssa-app-link-redirect-error-message">{errorMessage}</h2>
        </div>
      );
    }

    return (
      <div className="u-els-padding-3x">
        <h2 className="c-els-heading c-els-heading--h4 u-els-color-primary" data-testid="ssa-app-link-redirect-loading-message"><ELSPageLoader /></h2>
      </div>
    );
  }
}

export default connector(AppLinkRedirect);
